import {conversationText, type ConversationDisplayCopy} from "@/lib/i18n/conversation-copy"
/**
 * One resolver for the list item and the thread header: two copies of this chain
 * drifted before, and "Cory" in one view and "Unknown" in the other reads as a bug.
 */

import { customerPhoneFromExternalUserId } from './channels/channel-user-identity'
import type { CapturedCustomerInfo, ChannelType } from './types'

export interface ContactTitleSource {
  contact_name: string | null
  customer_info: CapturedCustomerInfo | null
  /** Platform handle; on Instagram `contact_name` holds a copy of it, not a name. */
  contact_handle?: string | null
  channel_type?: ChannelType
  external_user_id?: string | null
}

export interface ContactTitle {
  copy?: ConversationDisplayCopy
  title: string
  /** The email, only when it is not already the title. */
  subtitle: string | null
  /**
   * The customer's number for display: captured wins over the one the channel id
   * implies (WhatsApp wa_id) — same precedence as the AI prompt and the CRM
   * contact. Omitted when it is already the title. NOT the exclusion key: typed
   * phone entries match the channel id alone (see `exclusionIdentity`).
   */
  phone: string | null
}

/**
 * Channel name first — except on channels that store the USERNAME there
 * (Instagram), where it is an identifier, not a name. Letting it win would hide
 * the captured name behind a handle the row already shows on its own line, and
 * would title Instagram rows differently from every other channel.
 */
function resolveName(conversation: ContactTitleSource): string | null {
  const channelName = present(conversation.contact_name)
  const capturedName = present(conversation.customer_info?.name)
  const handle = present(conversation.contact_handle)
  const channelNameIsHandle =
    !!handle && !!channelName && channelName.toLowerCase() === handle.toLowerCase()

  return channelNameIsHandle ? capturedName ?? channelName : channelName ?? capturedName
}

/** `capture_customer_info` can write an empty string, which is not a known value. */
const present = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export function resolveContactTitle(conversation: ContactTitleSource): ContactTitle {
  const name = resolveName(conversation)
  const email = present(conversation.customer_info?.email)
  const phone = present(conversation.customer_info?.phone)
  const channelPhone = conversation.channel_type
    ? customerPhoneFromExternalUserId(conversation.channel_type, conversation.external_user_id)
    : null
  const knownPhone = phone ?? channelPhone

  const identify = (title: string, subtitle: string | null): ContactTitle => ({
    title,
    subtitle,
    phone: knownPhone && knownPhone !== title ? knownPhone : null,
  })

  if (name) return identify(name, email)
  if (email) return identify(email, null)
  if (knownPhone) return identify(knownPhone, null)
  return {...identify(conversationText({key: 'unknownContact'}), null), copy: {title: {key: 'unknownContact'}}}
}
